import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import { Page, PageHeader, Card, SearchInput, Badge, EmptyRow, Alert, Loading } from '../../ui';
import { api } from '../../ui/api';

/**
 * The audits and bookings lists share this layout. `idKey` names the human ID field,
 * `statusOf` maps a status code to {label, tone}, and `groups` lists the filter chips
 * as [name, status codes].
 */
const JobList = ({ title, subtitle, endpoint, idKey, statusOf, detailPath, groups, dateLabel }) => {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('All');
  const navigate = useNavigate();

  useEffect(() => {
    api(endpoint).then((data) => setRows([...data].reverse())).catch((e) => { setError(e.message); setRows([]); });
  }, [endpoint]);

  const filters = [['All', null], ...groups];
  const inGroup = (name, code) => {
    const codes = filters.find(([n]) => n === name)[1];
    return codes === null || codes.includes(code);
  };
  const q = query.toLowerCase();
  const shown = (rows || []).filter((r) => inGroup(filter, r.status_code) && (
    (r[idKey] || '').toLowerCase().includes(q)
    || ((r.user && r.user.username) || '').toLowerCase().includes(q)
    || ((r.service && r.service.service_name) || '').toLowerCase().includes(q)));
  const count = (name) => (rows || []).filter((r) => inGroup(name, r.status_code)).length;

  return (
    <Page>
      <PageHeader title={title} subtitle={subtitle} />
      <Alert>{error}</Alert>
      <Card flush>
        <div className="toolbar">
          <SearchInput value={query} onChange={setQuery} placeholder="Search by ID, customer or service" />
          <div className="chips">
            {filters.map(([name]) => (
              <button key={name} className={`chip${filter === name ? ' on' : ''}`} onClick={() => setFilter(name)}>
                {name}{rows ? ` · ${count(name)}` : ''}
              </button>
            ))}
          </div>
        </div>
        {!rows ? <Loading /> : (
          <div className="table-wrap">
            <table className="table stack-sm">
              <thead>
                <tr>
                  <th>ID / {dateLabel.toLowerCase()}</th>
                  <th>Customer</th>
                  <th>Service</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.length ? shown.map((row) => {
                  const s = statusOf(row.status_code);
                  return (
                    <tr key={row.id} className="clickable" onClick={() => navigate(`${detailPath}/${row.id}`)}>
                      <td data-label="ID" className="nowrap">
                        <Link to={`${detailPath}/${row.id}`} className="mono strong" onClick={(e) => e.stopPropagation()}>{row[idKey]}</Link>
                        <span className="sub">{row.date || '—'}</span>
                      </td>
                      <td data-label="Customer">
                        <span className="strong">{row.user ? row.user.username : '—'}</span>
                        <span className="sub">{row.user ? row.user.phone_number : ''}</span>
                      </td>
                      <td data-label="Service" className="clip">
                        <span className="ellipsis">{row.service ? row.service.service_name : '—'}</span>
                        <span className="sub">{row.service && row.service.category ? row.service.category.name : ''}</span>
                      </td>
                      <td data-label="Status"><Badge tone={s.tone}>{s.label}</Badge></td>
                      <td className="actions-cell"><span className="icon-btn"><FaChevronRight /></span></td>
                    </tr>
                  );
                }) : <EmptyRow cols={5}>{rows.length ? 'Nothing matches these filters.' : 'Nothing here yet.'}</EmptyRow>}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </Page>
  );
};

export default JobList;
